import React, { useState } from 'react';
import classNames from 'classnames';

import { useIntl, FormattedMessage } from '../../util/reactIntl';
import { propTypes } from '../../util/types';

import { Avatar, ReviewRating, UserDisplayName } from '../../components';
import ShowMoreText from 'react-show-more-text';

import css from './Reviews.module.css';

const DEFAULT_REVIEWS_COUNT = 5;

const Review = props => {
  const { review, intl } = props;

  const date = review.attributes.createdAt;
  const dateString = intl.formatDate(date, { month: 'long', year: 'numeric' });

  return (
    <div className={css.review}>
      <p className={css.reviewContent}>
        <ShowMoreText
          lines={6}
          more="Show more"
          less="Show less"
          className={css.reviewText}
          expanded={false}
          truncatedEndingComponent={'... '}
        >
          {review.attributes.content}
        </ShowMoreText>
      </p>
      <p className={css.reviewInfo}>
        <span className={css.desktopReviewRatingWrapper}>
          <ReviewRating
            rating={review.attributes.rating}
            className={css.desktopReviewRating}
            reviewStarClassName={css.reviewRatingStar}
          />
        </span>
        <span className={css.rating}>{`${review.attributes.rating}/5`}</span>
      </p>
    </div>
  );
};

/**
 * A component that renders a list of reviews.
 *
 * @component
 * @param {Object} props
 * @param {string} [props.rootClassName] - Custom class that overrides the default class for the root element
 * @param {string} [props.className] - Custom class that extends the default class for the root element
 * @param {Array<propTypes.review>} props.reviews - The reviews to render
 * @returns {JSX.Element}
 */
const Reviews = props => {
  const [visibleCount, setVisibleCount] = useState(DEFAULT_REVIEWS_COUNT);
  const intl = useIntl();
  const { className, rootClassName, reviews = [] } = props;
  const classes = classNames(rootClassName || css.root, className);

  const totalRating = reviews.reduce((acc, review) => acc + review.attributes.rating, 0);
  const averageRating = reviews.length > 0 ? totalRating / reviews.length : 0;

  const visibleItems = reviews.slice(0, visibleCount);
  const hasMore = visibleCount < reviews.length;
  const showLess = reviews.length > DEFAULT_REVIEWS_COUNT;
  const handleShowMoreToggle = value => {
    value
      ? setVisibleCount(DEFAULT_REVIEWS_COUNT)
      : setVisibleCount(prevCount => prevCount + DEFAULT_REVIEWS_COUNT);
  };

  return (
    <div>
      <ul className={classes}>
        <li className={css.reviewItem}>
          <div className={css.reviewStats}>
            <div className={css.totalRating}>{`${averageRating.toFixed(1)} / 5`}</div>
            <ReviewRating
              rating={averageRating}
              className={css.totalReviewRating}
              reviewStarClassName={css.reviewRatingStar}
            />
          </div>
          <div className={css.totalReviewsCount}>
            <FormattedMessage id="ListingPage.totalReviews" values={{ count: reviews.length }} />
          </div>
        </li>
        {visibleItems.map(r => {
          return (
            <li key={`Review_${r.id.uuid}`} className={css.reviewItem}>
              <Review review={r} intl={intl} />
            </li>
          );
        })}
      </ul>
      {hasMore ? (
        <div className={css.showMoreToggle} onClick={() => handleShowMoreToggle()}>
          Show more
        </div>
      ) : showLess ? (
        <div
          className={css.showMoreToggle}
          onClick={() => handleShowMoreToggle(DEFAULT_REVIEWS_COUNT)}
        >
          Show less
        </div>
      ) : null}
    </div>
  );
};

export default Reviews;
